import React from 'react';
import { Box, CardContent, Typography, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import StyledCard, { PrimaryButton, OutlineButton } from './ui/Styled';
import { useDropzone } from 'react-dropzone';
import type { Project } from '../services/api';

interface ProjectCardProps {
  project: Project;
  onOpenInspections: (projectId: string) => void;
  onStartScan: (projectId: string) => void;
  onDropImage: (projectId: string, file: File) => void;
}

export default function ProjectCard({
  project,
  onOpenInspections,
  onStartScan,
  onDropImage,
}: ProjectCardProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    noClick: true, // card click should not open file picker — buttons handle their own clicks
    noKeyboard: true,
    onDrop: (files) => {
      const file = files[0];
      if (file) onDropImage(project.id, file);
    },
  });

  const theme = useTheme();

  return (
    <StyledCard
      {...getRootProps()}
      data-testid={`project-card-${project.id}`}
      data-drag-active={isDragActive ? 'true' : 'false'}
      sx={{
        border: `1px solid ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.04)}`,
        boxShadow: isDragActive ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.14)}` : 'none',
        height: '100%',
        transition: 'all 0.15s',
      }}
    >
      <input {...getInputProps()} />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: 'text.primary', fontWeight: 'bold', fontSize: 16 }}>
            {project.name}
          </Typography>
          <Chip label="Active" size="small" sx={{ bgcolor: 'success.dark', color: 'success.main', fontSize: 11 }} />
        </Box>
        {project.client_name && (
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 0.5 }}>
            Client: {project.client_name}
          </Typography>
        )}
        {project.address && (
          <Typography sx={{ color: 'text.secondary', fontSize: 12, mb: 1 }}>📍 {project.address}</Typography>
        )}
        {project.description && (
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>{project.description}</Typography>
        )}
        {isDragActive && (
          <Typography data-testid="project-card-drop-hint" sx={{ color: 'primary.main', fontSize: 12, fontWeight: 600, mb: 1 }}>
            Drop image to start a new inspection
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <PrimaryButton size="small" onClick={() => onOpenInspections(project.id)} sx={{ fontSize: 12, width: '100%' }}>
            📋 Inspections
          </PrimaryButton>
          <OutlineButton size="small" onClick={() => onStartScan(project.id)} sx={{ width: '100%', fontSize: 12 }}>
            📷 Scan
          </OutlineButton>
        </Box>
      </CardContent>
    </StyledCard>
  );
}
